import { database, ref, get } from "../config/firebase";
import { gamificationService } from "./gamificationService";

export interface PublicAchievement {
  id: string;
  name: string;
  icon: string;
  description?: string;
}

export interface PublicProfileData {
  uid: string;
  displayName: string;
  photoURL?: string;
  isDeaf: boolean;
  age?: number;
  gender?: string;
  country?: string;
  state?: string;
  city?: string;
  points: number;
  level: number;
  streakDays: number;
  achievements: PublicAchievement[];
}

function toAchievementList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    return Object.keys(raw as object)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => (raw as Record<string, unknown>)[k]);
  }
  return [];
}

function achievementId(item: Record<string, unknown>): string {
  if (typeof item.id === "string") return item.id;
  if (typeof item.id === "number") return String(item.id);
  return "";
}

function normalizeAchievements(raw: unknown): PublicAchievement[] {
  return toAchievementList(raw)
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === "object"
    )
    .map((item) => {
      const id = achievementId(item);
      const name = typeof item.name === "string" ? item.name : "Conquista";
      const icon = typeof item.icon === "string" ? item.icon : "🏆";
      const description =
        typeof item.description === "string" ? item.description : undefined;
      return { id, name, icon, description };
    })
    .filter((a) => a.id || a.name !== "Conquista");
}

function resolvePhotoURL(data: Record<string, unknown>): string | undefined {
  const base64 = data.photoBase64;
  if (typeof base64 === "string" && base64.trim()) {
    const trimmed = base64.trim();
    if (trimmed.startsWith("data:")) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
  }
  const { photoURL } = data;
  if (typeof photoURL === "string" && photoURL) return photoURL;
  const { avatar } = data;
  if (typeof avatar === "string" && avatar) return avatar;
  return undefined;
}

export const publicProfileService = {
  async getPublicProfile(uid: string): Promise<PublicProfileData | null> {
    if (!uid) return null;
    try {
      const snapshot = await get(ref(database, `users/${uid}`));
      if (!snapshot.exists()) return null;

      const data = snapshot.val() as Record<string, unknown>;
      const points = Number(data.points) || 0;
      const level =
        Number(data.level) || gamificationService.calculateLevel(points);

      return {
        uid,
        displayName:
          (typeof data.displayName === "string" && data.displayName) ||
          (typeof data.name === "string" && data.name) ||
          "Usuário",
        photoURL: resolvePhotoURL(data),
        isDeaf: Boolean(data.isDeaf),
        age: data.age != null ? Number(data.age) : undefined,
        gender: typeof data.gender === "string" ? data.gender : undefined,
        country: typeof data.country === "string" ? data.country : undefined,
        state: typeof data.state === "string" ? data.state : undefined,
        city: typeof data.city === "string" ? data.city : undefined,
        points,
        level,
        streakDays: Number(data.streakDays) || 0,
        achievements: normalizeAchievements(data.achievements),
      };
    } catch (error) {
      console.error("Erro ao buscar perfil público:", error);
      return null;
    }
  },
};
